import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import api from "../../../api/axios";
import OrganizationSelect from "../components/OrganizationSelect/OrganizationSelect";

export default function CreativesPage() {
  const [creatives, setCreatives] = useState([]);
  const [search, setSearch] = useState("");
  const [organization, setOrganization] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCreatives();
  }, []);

  const loadCreatives = async (orgId = "") => {
    try {
      setLoading(true);
      const params = {};

      if (orgId) {
        params.org_id = orgId;
      }

      const response = await api.get("/creative/generate-video/list", { params });
      if (response && response.data && response.data.success) {
        setCreatives(response.data?.data || []);
        setLoading(false);
      } else {
        setLoading(false);
        setCreatives([]);
      }
    } catch (err) {
      setLoading(false);
      console.log(err?.data?.message);
    }
  };

  const rows = useMemo(() => {
    return creatives.filter((item) =>
      (item.creative_name || "")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [creatives, search]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Creatives</h2>
        <p className="mt-1 text-sm text-slate-500">
          Monitor creatives created across all organizations.
        </p>
      </div>
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex gap-3 border-b p-4">
          <label className="relative flex-1">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search creatives…"
              className="w-full rounded-lg border px-9 py-2 text-sm"
            />
          </label>
          <OrganizationSelect
            value={organization}
            onChange={(orgId) => {
              setOrganization(orgId);
              loadCreatives(orgId);
            }}
            className="w-56"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Organization</th>
                <th className="px-5 py-3">Creative</th>
                <th className="px-5 py-3">Brand</th>
                <th className="px-5 py-3">Campaign</th>
                <th className="px-5 py-3">Layout</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3">Video</th>
                <th className="px-5 py-3">XML</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">

              {loading && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-500">
                    Loading creatives...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-500">
                    No creatives found.
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((item) => (
                  <tr
                    key={item.id}
                    className="transition-colors hover:bg-slate-50"
                  >

                    <td className="px-5 py-4">
                      {item.org_name || "-"}
                    </td>

                    {/* Creative */}
                    <td className="px-5 py-4">
                      <div>
                        <div className="font-semibold text-slate-800">
                          {item.creative_name}
                        </div>

                        <div className="mt-1 text-xs text-slate-500">
                          {item.domain}
                        </div>
                      </div>
                    </td>

                    {/* Brand */}
                    <td className="px-5 py-4">
                      {item.brand_name || "-"}
                    </td>

                    {/* Campaign */}
                    <td className="px-5 py-4">
                      {item.camp_name || "-"}
                    </td>

                    {/* Layout */}
                    <td className="px-5 py-4">
                      <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                        {item.layout}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${item.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : item.status === "processing"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                          }`}
                      >
                        {item.status}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-5 py-4">
                      <div>
                        {new Date(item.created_on).toLocaleDateString()}
                      </div>

                      <div className="text-xs text-slate-400">
                        {new Date(item.created_on).toLocaleTimeString()}
                      </div>
                    </td>

                    {/* Video */}
                    <td className="px-5 py-4">
                      {item.bunny_url ? (
                        <a
                          href={item.bunny_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:underline"
                        >
                          Preview
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>

                    {/* XML */}
                    <td className="px-5 py-4">
                      {item.vast_xml_url ? (
                        <a
                          href={item.vast_xml_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-600 hover:underline"
                        >
                          XML
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
